import { type MouseEventHandler, memo, useCallback, useEffect, useRef } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';

import { type SelectionMouseModifiers } from '~/hooks/useSelectableCard';

export type ContentCollectionGalleryAssetButtonProps = {
  preSelectItem: (modifiers: SelectionMouseModifiers) => void;
  confirmItemSelection: (modifiers: SelectionMouseModifiers) => void;
  isSelectable: boolean;
  onClick?: () => void;
};

export const ContentCollectionGalleryAssetButton = memo(
  ({ preSelectItem, confirmItemSelection, isSelectable, onClick }: ContentCollectionGalleryAssetButtonProps) => {
    const { isAboveMediumScreen } = useBreakpointsContext();
    const isMouseDownRef = useRef(false);

    const onMouseUp = useCallback(
      (event: MouseEvent) => {
        isMouseDownRef.current = false;

        if (isAboveMediumScreen && isSelectable) {
          confirmItemSelection({ shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey });
        }
      },
      [confirmItemSelection, isAboveMediumScreen, isSelectable],
    );

    useEffect(() => {
      if (isMouseDownRef.current) {
        window.addEventListener('mouseup', onMouseUp, { once: true });
      }
      return () => {
        window.removeEventListener('mouseup', onMouseUp);
      };
    }, [onMouseUp]);

    const onMouseDown = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        if (event.button === 0) {
          if (!isAboveMediumScreen || !isSelectable) return;

          isMouseDownRef.current = true;

          preSelectItem({ shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey });

          window.addEventListener('mouseup', onMouseUp, { once: true });
        }
      },
      [isAboveMediumScreen, isSelectable, onMouseUp, preSelectItem],
    );

    return (
      <button
        data-testid="CONTENT_COLLECTION_GALLERY_SELECT_ASSET_BUTTON"
        className="absolute inset-0 w-full"
        onClick={!isAboveMediumScreen || !isSelectable ? onClick : undefined}
        onMouseDown={onMouseDown}
        type="button"
      />
    );
  },
);

ContentCollectionGalleryAssetButton.displayName = 'ContentCollectionGalleryAssetButton';
